import {
	Grid,
	styled,
	Typography,
	InputAdornment,
	TextField,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { debounce } from 'lodash';

import {
	getFilterHeaderValues,
	getFiltersValues,
	getRegulationInventoryData,
} from 'common/api';
import {
	IAllFilterResp,
	IAllFilterType,
	IFilterKeysParams,
	IFilterKeysResponse,
	ITabValues,
} from 'common/api/type-def';
import { ReactComponent as FilterIcon } from 'common/assets/images/Filter.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { ReactComponent as Export } from 'common/assets/images/vector-export.svg';
import Banner from 'common/components/Banner/Banner';
import ButtonComp from 'common/components/Button/ButtonComp';
import AdvancedFilters, {
	IAdvancedFiltersOptions,
	IAdvancedFilterValueOptions,
} from 'common/components/Filters/AdvancedFilters';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import TabBar from 'common/components/TabBar/TabBar';
import { FILTER_LABELS, MODULES, UPLOAD_LABEL } from 'common/constants';
import {
	BANNER_TITLE,
	DATAINGESTIONTABS,
	FILTER_KEYS,
	SHOW_ONLY_UPLOADED_BY_ME,
} from 'common/constants/DataIngestionConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RELATION_MAPPING_TAB_VALUES } from 'common/constants/TaxonomyConstants';
import { APP_GENERAL_ERROR_MSG, PWC } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setFileDrawer,
	setToastMessage,
	setUploadParserSelectedTab,
	setParserHighlights,
	setHighlightButtonText,
	setParserEndHighlightId,
	setParserDocumentInfo,
} from 'common/store/reducer/commonReducer';
import {
	setDataIngestionFilterKeys,
	setDataIngestionSelectedFilterKeys,
	setDataIngestionAssignedToMeChecked,
} from 'common/store/reducer/data-ingestion-reducer';
import {
	formatNumber,
	toSentenceCase,
	filterDataHandler,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { initiateProcessUpload } from './api/index';
import DataIngestionDataTable from './DataIngestionDataTable/DataIngestionDataTable';
import { updateTabName } from './DataIngestionUtils';
import FileUploadComp from './FileUpload/FileUploadComp';

import classes from './DataIngestion.module.scss';

const {
	buttonHoverTextColor,
	searchFieldBackground,
	searchFieldBorderRadius,
	checkboxCheckedcolor,
} = classes;

const StyledSearchTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '100%',
});

interface ITabsData {
	allTab: ITabsDetails;
	processingTab: ITabsDetails;
	validatingTab: ITabsDetails;
	failedTab: ITabsDetails;
	completedTab: ITabsDetails;
}

interface ITabsDetails {
	data: any;
	count: number;
}

const defaultTabValues: ITabsData = {
	allTab: { data: [], count: 0 },
	processingTab: { data: [], count: 0 },
	validatingTab: { data: [], count: 0 },
	failedTab: { data: [], count: 0 },
	completedTab: { data: [], count: 0 },
};

const { DATA_INGESTION } = MODULES;
const { POLICIES } = RELATION_MAPPING_TAB_VALUES;
const DataIngestion = () => {
	const dispatch = useDispatch();

	const { ADVANCED_FILTERS } = FILTER_LABELS;

	const { STATUS } = FILTER_KEYS;
	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'dataIngestionTable',
	});
	const { REACT_APP_ENVIRONMENT } = process.env;

	const fileMappingData = useSelector(
		(state: RootState) => state.dataingestion.fileMappingData,
	);
	const dataIngestionTabIndex = useSelector(
		(state: RootState) =>
			state?.common?.selectedDataIngestionTab.dataIngestionTabIndex,
	);

	const fileDrawerState = useSelector(
		(state: RootState) => state.common.fileDrawer,
	);

	const filterKeys = useSelector(
		(state: RootState) => state.dataingestion.dataIngestionFilterKeys,
	);

	const selectedFilterKeys = useSelector(
		(state: RootState) => state.dataingestion.dataIngestionSelectedFilterKeys,
	);

	const showAssignedToChecked = useSelector(
		(state: RootState) => state.dataingestion.showAssignedMeChecked,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const { OK, BAD_REQUEST, INTERNAL_SERVER_ERROR } = HTTP_STATUS_CODES;

	const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] =
		useState<boolean>(false);

	const [filterHeaders, setFilterHeaders] = useState<IFilterKeysResponse[]>([]);

	const [filterValues, setFilterValues] = useState<IAdvancedFiltersOptions[]>(
		[],
	);

	const [isFilterOptionsLoading, setIsFilterOptionsLoading] =
		useState<boolean>(false);
	const customDateDetailKeys = ['activityDate'];

	const handleFileUpload = useCallback(() => {
		dispatch(setFileDrawer(true));
		setShowFileUpload(true);
	}, []);
	const handleStartProcessing = async (dataType: string) => {
		setIsDataLoading(true);
		dispatch(setFileDrawer(false));
		if (dataType !== POLICIES) {
			const paramsData: any = { ...fileMappingData };
			paramsData.userEmail = loggedInUser?.email;
			const resp = await initiateProcessUpload(paramsData);
			if (resp?.statusCode === OK) {
				handleResponse(dataType);
			} else {
				handleResponse(dataType);
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} else {
			handleResponse(dataType);
		}

		setTimeout(() => {
			setIsUploadCompleted(true);
		}, 7000);
	};
	const handleResponse = (dataType: string) => {
		if (dataType === POLICIES) {
			handleDataIngestionTabChange(0);
		} else {
			handleDataIngestionTabChange(1);
		}
	};
	const { ALL, PROCESSING, VALIDATING, FAILED, COMPLETED } = DATAINGESTIONTABS;

	const [tabsData, setTabsData] = useState<ITabsData>(defaultTabValues);

	const [searchText, setSearchText] = useState<string>('');

	const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
	const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
	const [isUploadCompleted, setIsUploadCompleted] = useState<boolean>(false);

	const defaultPageQueryParams = { pageNumber: 0, pageSize: 10 };

	const handleApiResponse = (resp: any, tabName: string) => {
		if (resp?.statusCode === OK && resp?.responseBody) {
			const respData = resp?.responseBody;
			const tempTableData = respData?.content?.length ? respData.content : [];
			const response = {
				data: tempTableData,
				count: respData?.totalCount || 0,
			};
			handleTabData(tabName, response);
		} else if (
			resp?.statusCode === BAD_REQUEST ||
			resp?.statusCode === INTERNAL_SERVER_ERROR
		) {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsDataLoading(false);
	};

	const statusFilter = (filters: any) =>
		filters.filter((res: any) => res.key === STATUS)[0];

	const getFilteredValues = (tabName: string, response: any) => {
		const selectedFilters = statusFilter(selectedFilterKeys);
		if (selectedFilters?.values.includes(tabName)) {
			const filteredResponse = response.data.filter(
				(res: any) => res.status === tabName,
			);
			return {
				count: response.count,
				data: filteredResponse,
			};
		}
		return { count: 0, data: [] };
	};

	const handleTabData = (tabName: string, response: any) => {
		switch (tabName) {
			case ALL:
				setTabsData((prev) => ({ ...prev, allTab: response }));
				break;
			case PROCESSING:
				setTabsData((prev) => ({
					...prev,
					processingTab: getFilteredValues(tabName, response),
				}));
				break;
			case VALIDATING:
				setTabsData((prev) => ({
					...prev,
					validatingTab: getFilteredValues(tabName, response),
				}));
				break;
			case FAILED:
				setTabsData((prev) => ({
					...prev,
					failedTab: getFilteredValues(tabName, response),
				}));
				break;
			case COMPLETED:
				setTabsData((prev) => ({
					...prev,
					completedTab: getFilteredValues(tabName, response),
				}));
				break;
			default:
				break;
		}
	};

	const getTabNamesFilter = (tabName: string) => {
		const statusFilters = statusFilter(selectedFilterKeys);
		if (tabName === ALL) {
			if (statusFilters?.values?.length > 0) {
				return statusFilters?.values;
			}
			return [VALIDATING, PROCESSING, FAILED, COMPLETED];
		}
		return [tabName];
	};

	const handleSearch = (data: string) => {
		setSearchText(data);
	};

	const debouncedSearchHandler = debounce(handleSearch, 500);

	const fetchDataForTab = async (data: any, tabName: string) => {
		const apiData = { ...data };
		const modifiedFilters = selectedFilterKeys.filter(
			(res: any) => res.key !== STATUS,
		);
		const filters = [
			...modifiedFilters,
			{
				key: STATUS,
				values: getTabNamesFilter(tabName),
			},
		];
		const paramsObj = {
			searchText,
			index: `prc-ingestion-index-${REACT_APP_ENVIRONMENT}`,
			filter: filters,
		};
		setIsDataLoading(true);
		const resp = await getRegulationInventoryData(
			paramsObj,
			apiData,
			MODULES.DATA_INGESTION,
		);
		handleApiResponse(resp, tabName);
	};

	const getAllTabData = async (data: any) => {
		fetchDataForTab(data, ALL);
	};

	const getProcessingTabData = async (data: any) => {
		fetchDataForTab(data, PROCESSING);
	};

	const getValidatingTabData = async (data: any) => {
		fetchDataForTab(data, VALIDATING);
	};

	const getFailedTabData = async (data: any) => {
		fetchDataForTab(data, FAILED);
	};

	const getCompletedTabData = async (data: any) => {
		fetchDataForTab(data, COMPLETED);
	};

	const fetchAllPRCIngestionTableData = async () => {
		getAllTabData(tableQueryObj);
		getProcessingTabData(tableQueryObj);
		getValidatingTabData(tableQueryObj);
		getFailedTabData(tableQueryObj);
		getCompletedTabData(tableQueryObj);
		setIsUploadCompleted(false);
	};

	const dataIngestionTabs: ITabValues[] = [
		{
			name: ALL,
			count: formatNumber(tabsData.allTab.count).toString(),
		},
		{
			name: VALIDATING,
			count: formatNumber(tabsData.validatingTab.count).toString(),
		},
		{
			name: PROCESSING,
			count: formatNumber(tabsData.processingTab.count).toString(),
		},
		{
			name: FAILED,
			count: formatNumber(tabsData.failedTab.count).toString(),
		},
		{
			name: COMPLETED,
			count: formatNumber(tabsData.completedTab.count).toString(),
		},
	];

	const advancedFilterButtonClicked = async () => {
		setShowFileUpload(false);
		getAdvancedFilterOptions();
	};

	const getAdvancedFilterOptions = async () => {
		const relationship = filterHeaders.map((res) => res.filterKey);
		const params: IAllFilterType = {
			searchText,
			index: 'prc-ingestion-index',
			filter: selectedFilterKeys,
			relationship,
			source: PWC,
		};
		setIsFilterOptionsLoading(true);
		const response = await getFiltersValues(params, DATA_INGESTION);
		if (response.statusCode === OK && response.responseBody) {
			setIsAdvancedFilterOpen(true);
			dispatch(setFileDrawer(true));
			setIsFilterOptionsLoading(false);
			generateFilterValues(response.responseBody);
		}
	};

	const allTabsStatus = [
		{ label: VALIDATING, value: VALIDATING, type: STATUS },
		{ label: PROCESSING, value: PROCESSING, type: STATUS },
		{ label: FAILED, value: FAILED, type: STATUS },
		{ label: COMPLETED, value: COMPLETED, type: STATUS },
	];

	const getFilterName = (key: string) =>
		filterHeaders.filter((res) => res.filterKey === key)[0].filterName;

	const getFilterValues = (key: string, values: string[]) =>
		values.map((res) => ({
			label: res,
			value: res,
			type: key,
		}));

	const generateFilterValues = (data: IAllFilterResp) => {
		const filtersExcludingStatus = data.relationship.filter(
			(res) => res.objectName !== STATUS,
		);
		const filterOptions: IAdvancedFiltersOptions[] = filtersExcludingStatus.map(
			(res) => ({
				filterKey: res.objectName,
				filterTitle: getFilterName(res.objectName),
				filterValues: getFilterValues(
					res.objectName ?? '',
					res.objectValues as string[],
				),
			}),
		);
		filterOptions.push({
			filterKey: STATUS,
			filterTitle: getFilterName(STATUS),
			filterValues: allTabsStatus,
		});
		setFilterValues(filterOptions);
	};

	const getAdvancedFilterHeaderValues = async () => {
		const params: IFilterKeysParams = {
			dataSource: PWC,
			module: 'PRC',
		};
		const response = await getFilterHeaderValues(params);
		if (response.statusCode === OK && response.responseBody) {
			setFilterHeaders(response.responseBody);
		}
	};

	const onCloseAdvancedFilters = () => {
		setIsAdvancedFilterOpen(false);
		dispatch(setFileDrawer(false));
	};

	const handleFiltersApplied = (data: IAdvancedFilterValueOptions[]) => {
		dispatch(setDataIngestionFilterKeys(data));
		const filters = filterDataHandler(
			data,
			showAssignedToChecked,
			loggedInUser,
			'user',
		);
		const selectedStatusFilter = statusFilter(filters);
		if (selectedStatusFilter?.length > 0) {
			dispatch(setDataIngestionSelectedFilterKeys(filters));
		} else {
			dispatch(
				setDataIngestionSelectedFilterKeys([
					...filters,
					{ key: STATUS, values: [PROCESSING, VALIDATING, FAILED, COMPLETED] },
				]),
			);
		}
		setIsAdvancedFilterOpen(false);
		dispatch(setFileDrawer(false));
	};

	const handleClearAll = () => {
		dispatch(
			setDataIngestionSelectedFilterKeys([
				{ key: STATUS, values: [VALIDATING, PROCESSING, FAILED, COMPLETED] },
			]),
		);
		dispatch(setDataIngestionFilterKeys([]));
		dispatch(setDataIngestionAssignedToMeChecked(false));
	};

	useEffect(() => {
		updateTableQuery(defaultPageQueryParams);
		fetchAllPRCIngestionTableData();
	}, [searchText, selectedFilterKeys, dataIngestionTabIndex]);

	useEffect(() => {
		if (!fileDrawerState) {
			setShowFileUpload(false);
			setIsAdvancedFilterOpen(false);
		}
	}, [fileDrawerState]);

	useEffect(() => {
		if (isUploadCompleted && !showFileUpload) {
			updateTableQuery(defaultPageQueryParams);
			fetchAllPRCIngestionTableData();
		}
	}, [isUploadCompleted, showFileUpload]);

	useEffect(() => {
		getAdvancedFilterHeaderValues();
		dispatch(
			setDataIngestionSelectedFilterKeys([
				{ key: STATUS, values: [VALIDATING, PROCESSING, FAILED, COMPLETED] },
			]),
		);
		dispatch(setUploadParserSelectedTab(0));
		dispatch(setParserHighlights([]));
		dispatch(setHighlightButtonText('Highlight'));
		dispatch(
			setParserEndHighlightId({
				id: '',
				position: {
					boundingRect: { x1: 0, x2: 0, y1: 0, y2: 0, width: 0, height: 0 },
					pageNumber: 1,
					rects: [],
				},
				comment: { text: '', emoji: '' },
				content: {},
			}),
		);
		dispatch(
			setParserDocumentInfo({
				bookTitle: '',
				issuer: '',
				issuanceType: '',
				jurisdiction: '',
				issuanceDate: '',
				publishedDate: '',
			}),
		);
	}, []);

	const dataIngestionTabPanels = [
		{
			id: 1,
			children: (
				<DataIngestionDataTable
					isLoading={isDataLoading}
					id="dataIngestionTable"
					data={tabsData.allTab.data}
					fetchData={getAllTabData}
					pageCount={tabsData.allTab.count}
				/>
			),
		},
		{
			id: 2,
			children: (
				<DataIngestionDataTable
					isLoading={isDataLoading}
					id="dataIngestionTable"
					data={tabsData.validatingTab.data}
					fetchData={getValidatingTabData}
					pageCount={tabsData.validatingTab.count}
				/>
			),
		},
		{
			id: 3,
			children: (
				<DataIngestionDataTable
					isLoading={isDataLoading}
					id="dataIngestionTable"
					data={tabsData.processingTab.data}
					fetchData={getProcessingTabData}
					pageCount={tabsData.processingTab.count}
				/>
			),
		},
		{
			id: 4,
			children: (
				<DataIngestionDataTable
					isLoading={isDataLoading}
					id="dataIngestionTable"
					data={tabsData.failedTab.data}
					fetchData={getFailedTabData}
					pageCount={tabsData.failedTab.count}
				/>
			),
		},
		{
			id: 5,
			children: (
				<DataIngestionDataTable
					isLoading={isDataLoading}
					id="dataIngestionTable"
					data={tabsData.completedTab.data}
					fetchData={getCompletedTabData}
					pageCount={tabsData.completedTab.count}
				/>
			),
		},
	];

	const handleDataIngestionTabChange = (index: number) => {
		updateTabName(index, dispatch);
	};

	const assignedToMeChanged = (assignedToMe: boolean) => {
		dispatch(setDataIngestionAssignedToMeChecked(assignedToMe));
	};

	return (
		<Grid container data-testId="data-ingestion-page">
			<Banner>
				<Grid container style={{ padding: '20px' }}>
					<Grid item xs={12}>
						<Typography
							mb={1}
							className={classes['data-ingestion-banner-main-text']}
						>
							{toSentenceCase(BANNER_TITLE.BANNER_MAIN_TITLE)}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography
							mb={1}
							className={classes['data-ingestion-banner-sub-text']}
						>
							{toSentenceCase(BANNER_TITLE.BANNER_SUB_HEADING)}
						</Typography>
					</Grid>
					<Grid
						container
						spacing={1}
						item
						xs={12}
						sm={12}
						md={12}
						lg={12}
						xl={12}
						className={classes['banner-items-grid']}
					>
						<Grid item xs={12} sm={12} md={8} lg={8}>
							<StyledSearchTextField
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchOutlined
												className={classes['input-search-icon']}
											/>
										</InputAdornment>
									),
									inputProps: { 'data-testid': 'searchInput' },
								}}
								fullWidth
								placeholder="Search"
								size="small"
								onChange={(e: any) => debouncedSearchHandler(e?.target?.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={2} lg={2}>
							<ButtonComp
								btnType="secondary"
								variant="outlined"
								onClick={advancedFilterButtonClicked}
								data-testid="filter-icon"
								compClass={
									filterKeys.length > 0 || showAssignedToChecked
										? classes['advanced-fiter-button-filter-selected']
										: classes['advanced-fiter-button']
								}
							>
								<div className={classes['data-ingestion-filter-icon']}>
									<FilterIcon
										fill={
											filterKeys.length > 0 || showAssignedToChecked
												? buttonHoverTextColor
												: checkboxCheckedcolor
										}
									/>
								</div>
								<Typography>{ADVANCED_FILTERS}</Typography>
							</ButtonComp>
						</Grid>
						<Grid item xs={12} sm={12} md={2} lg={2}>
							<ButtonComp
								btnType="secondary"
								variant="outlined"
								onClick={handleFileUpload}
								data-testid="upload"
								compClass={classes['button-hover']}
							>
								<div className={classes['data-ingestion-filter-icon']}>
									<Export
										fill={checkboxCheckedcolor}
										className={classes['data-ingestion-export-icon']}
									/>
								</div>
								<Typography className={classes['upload-text']}>
									{UPLOAD_LABEL}
								</Typography>
							</ButtonComp>
						</Grid>
					</Grid>
				</Grid>
			</Banner>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<TabBar
					testId="dataIngestion-tab-testid"
					activeTab={dataIngestionTabIndex}
					details={dataIngestionTabs}
					showCount
					components={dataIngestionTabPanels}
					showButton={false}
					onTabChange={handleDataIngestionTabChange}
				/>
			</Grid>
			<SideDrawer
				className={classes['data-ingestion-drawer']}
				drawerState={fileDrawerState}
				customClass={`${classes['custom-width']} ${
					isAdvancedFilterOpen ? classes['data-ingestion-custom-drawer'] : ''
				} `}
			>
				{isAdvancedFilterOpen && !showFileUpload && (
					<AdvancedFilters
						filterOptionsData={filterValues}
						onClose={onCloseAdvancedFilters}
						applyFiltersHandler={handleFiltersApplied}
						handleClearFilter={handleClearAll}
						customDateDetailKeys={customDateDetailKeys}
						filterDataLoading={isFilterOptionsLoading}
						selectedListOptions={filterKeys}
						onCancel={onCloseAdvancedFilters}
						onAssignedToMeChecked={assignedToMeChanged}
						moduleType
						showingAssignedToMe={showAssignedToChecked}
						customShowOnlyAssignedMessage={SHOW_ONLY_UPLOADED_BY_ME}
					/>
				)}
				{showFileUpload && !isAdvancedFilterOpen && (
					<FileUploadComp onStartProcessing={handleStartProcessing} />
				)}
			</SideDrawer>
		</Grid>
	);
};

export default DataIngestion;
